import { deleteAnalysisItem, deleteChatHistoryItem } from '@services/analysisItems';
import { deleteShareTeamMember, removeTeamMember } from '@services/member';
import { deleteOrganization } from '@services/organization';
import { deleteOrgProduct } from '@services/orgProduct';
import { deleteReport, deleteReportSection } from '@services/report';
import { leaveStudy } from '@services/user';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSuccessToast } from '@utils/toast';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { bulkdeleteAIParticipant, deleteAIParticipant } from '../services/ai-participant';
import {
  addCodeFromCategory,
  deleteCategory,
  deleteInterviewCode,
  deleteInterviews,
  deleteSpeakerTag,
  deleteStudyTag,
  deleteTranscript,
  editTranscriptText,
  removeCodeFromCategory,
  removeInterviewCode,
} from '../services/interview';
import { deleteStudies, deleteTheme } from '../services/survey';

export const useRemoveMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ surveyId, email, study_type }) =>
      toast.promise(leaveStudy(surveyId, email, study_type), {
        loading: 'Leaving study...',
        success: 'Successfully left the study!',
        error: (error) => error?.detail || 'Error leaving study',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['surveys']);
    },
  });
};

export const useDeleteSurvey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteStudies(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      queryClient.invalidateQueries({ queryKey: ['recent_study'] });
      toast.success('Study deleted successfully!');
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to delete this study.');
      } else {
        toast.error('Error occurred while deleting study. Please try again later.');
      }
    },
  });
};

export const useDeleteInterviews = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteInterviews(id),
    onSuccess: () => {
      toast.success('Interview deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['qualitative_study'] });
      queryClient.invalidateQueries({ queryKey: ['recent_study'] });
    },
    onError: () => {
      toast.error('Error occurred while deleting study. Please try again later.');
    },
  });
};

export const useDeleteAIParticipant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      toast.promise(deleteAIParticipant(id), {
        loading: 'Deleting AI Participant...',
        success: 'AI Participant deleted successfully!',
        error: 'Error occurred while deleting AI Participant. Please try again later.',
        position: 'bottom-center',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_participants'] });
    },
  });
};

export const useBulkdeleteAIParticipants = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) =>
      toast.promise(bulkdeleteAIParticipant(ids), {
        loading: 'Deleting AI Participants...',
        success: 'AI Participants deleted successfully!',
        error: 'Error occurred while deleting AI Participants. Please try again later.',
        position: 'bottom-center',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_participants'] });
    },
  });
};
export const useDeleteTranscript = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteTranscript(id),

    onSuccess: () => {
      // Invalidate all transcript queries with pattern matching
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'transcripts',
      });
      toast.success('Transcript deleted successfully!');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.',
        { delay: 3000 },
      );
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to delete this study.');
      } else {
        toast.error('Error occurred while deleting study. Please try again later.');
      }
    },
  });
};
export const useDeleteCategory = (handleClosePopover) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => deleteCategory(payload),

    onSuccess: () => {
      handleClosePopover();
      queryClient.invalidateQueries({ queryKey: ['codebook'] });
      toast.success('Category deleted successfully!');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to delete this category.');
      } else {
        toast.error('Error occurred while deleting Category. Please try again later.');
      }
    },
  });
};
export const useRemoveCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => removeInterviewCode(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      // toast.success('Code removed successfully!');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to remove this code.');
      } else {
        toast.error('Error occurred while remove code. Please try again later.');
      }
    },
  });
};
export const useRemoveCodeFromCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => removeCodeFromCategory(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['codebook'] });
      // toast.success('Code removed successfully!');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to remove this code.');
      } else {
        toast.error('Error occurred while remove code. Please try again later.');
      }
    },
  });
};
export const useAddCodeFromCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => addCodeFromCategory(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['codebook'] });
      // toast.success('Code added successfully!');
      // toast.success(
      //   'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      // );
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to remove this code.');
      } else {
        toast.error('Error occurred while remove code. Please try again later.');
      }
    },
  });
};
export const useDeleteCode = (closeModal) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => deleteInterviewCode(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      closeModal();
      toast.success('Code deleted successfully!');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to delete this code.');
      } else {
        toast.error('Error occurred while deleting code. Please try again later.');
      }
    },
  });
};
export const useDeleteStudyTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tagId, valueId }) => deleteStudyTag({ id, tagId, valueId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['study-tags'] });
      toast.success(`Tag deleted successfully!`);
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to delete this tag.');
      } else {
        toast.error('Error occurred while deleting tag. Please try again later.');
      }
    },
  });
};
export const useDeleteReportSection = () => {
  return useMutation({
    mutationFn: ({ studyId, reportId, sectionId }) =>
      deleteReportSection({ studyId, reportId, sectionId }),

    onSuccess: () => {},
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to delete this study.');
      } else {
        toast.error('Error occurred while deleting study. Please try again later.');
      }
    },
  });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studyId, reportId }) =>
      toast.promise(deleteReport({ studyId, reportId }), {
        loading: 'Deleting report...',
        success: 'Report deleted successfully!',
        error: 'Error occurred while deleting report. Please try again later.',
        position: 'bottom-center',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
};
export const useDeleteSpeakerTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => deleteSpeakerTag(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      toast.success('Speaker tag deleted successfully!');
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to remove this code.');
      } else {
        toast.error('Error occurred while remove code. Please try again later.');
      }
    },
  });
};

export const useDeleteShareTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studyId, studyUrlType, email }) =>
      deleteShareTeamMember({ studyId, studyUrlType, email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-share-member'] });
      queryClient.invalidateQueries({ queryKey: ['team-member'] });
      showSuccessToast('Team member successfully deleted!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to delete team member. Please try again.');
    },
  });
};
export const useRemoveTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studyId, studyUrlType, email }) =>
      removeTeamMember({ studyId, studyUrlType, email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-share-member'] });
      queryClient.invalidateQueries({ queryKey: ['team-member'] });
      showSuccessToast('Team member successfully removed!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to remove team member. Please try again.');
    },
  });
};
export const useEditTranscriptText = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => editTranscriptText(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      toast.success('Transcript updated successfully!');
      toast.success(
        'Changes have been made. Run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
    onError: (error) => {
      if (error?.status === 403) {
        toast.error('Not authorized to eidt this transcript.');
      } else {
        toast.error('Error occurred while remove code. Please try again later.');
      }
    },
  });
};

export const useDeleteAnalysisItem = () => {
  return useMutation({
    mutationFn: ({ studyId, analysisItemId }) => deleteAnalysisItem({ studyId, analysisItemId }),
    onSuccess: () => {
      toast.success('Analysis item deleted successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to delete analysis item. Please try again.');
    },
  });
};
export const useDeleteChatHistory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId }) => deleteChatHistoryItem({ chatId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-history'] });
      toast.success('Chat item deleted successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to delete chat item. Please try again.');
    },
  });
};

export const useDeleteTheme = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => deleteTheme(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-question-theme'] });
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to delete theme. Please try again.');
    },
  });
};

export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (id) => deleteOrganization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('Organization deleted successfully!');
      navigate('/organizations');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to delete organization. Please try again.');
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (id) => deleteOrgProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product deleted successfully!');
      navigate('/products');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to delete product. Please try again.');
    },
  });
};
